import { tileKey, tileName, makeTile } from './tiles.js';
import { SUIT_NAMES, SUITS } from './constants.js';
import { claimOptions, allTilesOf, jokerExchangeOptions, applyJokerExchange } from './turn.js';
import { bestHands, handFit, validateWin } from './handMatch.js';

// AI now plays toward a real 2026 card hand: it picks the hand its tiles best
// fit, keeps tiles that hand needs, discards the rest, claims discards that
// complete a needed pung+, and declares Mahjong when its 14 tiles validate.
// All helpers are pure over (player, ...); rng drives only the beginner /
// intermediate randomness, exactly where the old monolith used Math.random.

function hasExposures(player) {
  return !!(player.exposed && player.exposed.length);
}

// The card hand this player is chasing: { hand, score, need }.
export function aiChooseTarget(player) {
  return bestHands(player.hand, hasExposures(player))[0];
}

export function aiCharlestonPick(player) {
  // Pass away the three tiles that least fit our best target hand.
  const scored = discardScores(player).slice().sort((a, b) => a.keep - b.keep);
  return scored.slice(0, 3).map((s) => s.t);
}

// NMJL: the second charleston happens only if all four players agree.
// Continue while the hand is still far from its best target; stop once the
// hand is taking shape (don't hand opponents anything once we're close).
export function aiWantsSecondCharleston(player) {
  return aiChooseTarget(player).score < 10;
}

// Courtesy pass offer: up to 3 true junk tiles (never jokers; keep-value 10
// means "no target hand wants this"). Offering fewer than 3 is normal.
export function aiCourtesyPick(player) {
  const scored = discardScores(player)
    .filter((s) => s.keep <= 10)
    .sort((a, b) => a.keep - b.keep);
  return scored.slice(0, 3).map((s) => s.t);
}

// Keep-value per tile: jokers top, then tiles the target still needs, then
// flowers, then off-target junk. Lowest keep-value is the best discard.
// `strong` mode blends the top three target hands (weights 4/2/1) so the AI
// keeps tiles with backup value instead of tunneling on a single hand.
export function discardScores(player, strong = false) {
  if (!strong) {
    const need = aiChooseTarget(player).need;
    const kept = new Map();
    return player.hand.map((t) => {
      if (t.type === 'joker') return { t, keep: 1000 };
      if (t.type === 'flower') return { t, keep: need.get('flower') ? 900 : 150 };
      const k = tileKey(t);
      const want = need.get(k) || 0;
      const used = kept.get(k) || 0;
      if (want > used) { kept.set(k, used + 1); return { t, keep: 500 + want }; } // a copy the hand needs
      return { t, keep: 10 }; // not needed, or an excess copy
    });
  }

  const targets = bestHands(player.hand, hasExposures(player)).slice(0, 3);
  const weights = [4, 2, 1];
  const keptPer = targets.map(() => new Map());
  return player.hand.map((t) => {
    if (t.type === 'joker') return { t, keep: 1000 };
    let keep = 0;
    const k = t.type === 'flower' ? 'flower' : tileKey(t);
    targets.forEach((target, i) => {
      const want = target.need.get(k) || 0;
      const used = keptPer[i].get(k) || 0;
      if (want > used) {
        keptPer[i].set(k, used + 1);
        keep += weights[i] * (100 + want);
      }
    });
    if (t.type === 'flower' && keep === 0) keep = 150; // flowers keep option value
    return { t, keep: keep || 10 };
  });
}

// Kept for callers/tests that still reference a generic tile value.
export function aiTileValue(t, hand) {
  const s = discardScores({ hand, exposed: [] }).find((x) => x.t === t);
  return s ? s.keep : 0;
}

// `visibleCounts` (optional): Map of tileKey → copies visible in discards and
// exposures. Advanced uses it to break keep-value ties toward "dead" tiles —
// copies nobody can claim anymore — instead of feeding live pungs/kongs.
export function aiPickDiscard(player, difficulty, rng, visibleCounts = null) {
  const hand = player.hand;
  if (hand.length === 0) return makeTile('flower');

  if (difficulty === 'beginner') {
    // Discard somewhere in the least-useful third — beatable but not silly.
    const scored = discardScores(player).sort((a, b) => a.keep - b.keep);
    const cut = Math.max(1, Math.ceil(scored.length / 3));
    return scored[Math.floor(rng() * cut)].t;
  }
  if (difficulty === 'intermediate') {
    // Was "one of the two worst"; now always the worst-fitting tile (the old
    // advanced behavior) — Georgianna outgrew the softer version.
    return discardScores(player).sort((a, b) => a.keep - b.keep)[0].t;
  }
  // Advanced: multi-target keep values, safest tile among the least useful.
  const scored = discardScores(player, true).sort((a, b) => a.keep - b.keep);
  if (visibleCounts) {
    const floor = scored[0].keep;
    const worst = scored.filter((s) => s.keep === floor);
    worst.sort((a, b) => {
      const va = a.t.type === 'flower' ? 0 : visibleCounts.get(tileKey(a.t)) || 0;
      const vb = b.t.type === 'flower' ? 0 : visibleCounts.get(tileKey(b.t)) || 0;
      return vb - va; // most-visible (deadest) first
    });
    return worst[0].t;
  }
  return scored[0].t;
}

// Copies of every tile visible to all players: the discard pool plus every
// exposed group. Feed to aiPickDiscard for defensive tie-breaking.
export function visibleTileCounts(state) {
  const counts = new Map();
  const add = (t) => {
    if (t.type === 'joker' || t.type === 'flower') return;
    const k = tileKey(t);
    counts.set(k, (counts.get(k) || 0) + 1);
  };
  for (const t of state.discards) add(t);
  for (const p of state.players) for (const g of p.exposed) for (const t of g.tiles) add(t);
  return counts;
}

export function aiExplainDiscard(player, tile) {
  if (tile.type === 'joker') return '(Unusual move!)';
  if (tile.type === 'wind') return "Winds aren't part of their plan.";
  if (tile.type === 'dragon') return 'Not collecting that dragon.';
  if (tile.type === 'flower') return "Doesn't need flowers for this hand.";
  if (tile.type === 'suited') {
    const suitCount = player.hand.filter((t) => t.type === 'suited' && t.suit === tile.suit).length;
    if (suitCount <= 1) return 'Clearing out ' + SUIT_NAMES[tile.suit] + 's.';
    return "Doesn't fit their hand.";
  }
  return '';
}

// Pick the exact exposure to claim: kong when the target wants four of this tile
// and a kong is formable (naturals + a joker), pung when it wants three — else
// none. `want` is the target hand's required count for the tile (3 = pung,
// 4 = kong); pairs/singles (<3) and quints+ (>4) are not claimed.
export function claimKindForNeed(options, want) {
  if (want === 4 && options.includes('kong')) return 'kong';
  if (want === 3 && options.includes('pung')) return 'pung';
  return null;
}

// Claim a discard only when it completes the exact group the target hand needs
// and the target is exposable (concealed hands cannot expose). Chooses kong over
// pung when the hand wants four — spending a joker only when the kong is needed.
export function aiDecideClaim(player, tile, difficulty) {
  if (difficulty === 'beginner') return null;
  const options = claimOptions(player, tile);
  if (options.length === 0) return null;
  const target = aiChooseTarget(player);
  if (!target || target.hand.concealed) return null;        // can't expose a concealed hand
  const want = target.need.get(tileKey(tile)) || 0;          // 0=not needed, 3=pung, 4=kong; quint(5)/pair(<3) → null
  const kind = claimKindForNeed(options, want);
  if (!kind) return null;
  // Never claim ourselves down to an empty concealed hand — the pair / singles a
  // hand needs must stay in hand; self-draw the rest.
  if (player.hand.length - (kind === 'kong' ? 3 : 2) < 1) return null;
  return kind;
}

// Perform every joker exchange that helps this player, mutating state.
// A joker is strictly more flexible than the natural handed over, so swap
// unless the target hand needs that natural in a slot a joker can't fill
// (pairs/singles, want 1–2) and we hold no spare copy. Returns the swaps
// made as [{ given, ownerIdx }] for commentary.
export function aiDoJokerSwaps(state, playerIdx) {
  const player = state.players[playerIdx];
  const swaps = [];
  for (let guard = 0; guard < 8; guard++) {
    const options = jokerExchangeOptions(state, playerIdx);
    const target = aiChooseTarget(player);
    const pick = options.find((o) => {
      const want = target.need.get(o.key) || 0;
      if (want === 0 || want >= 3) return true; // junk tile, or joker can stand in
      const held = player.hand.filter(
        (t) => t.type !== 'joker' && tileKey(t) === o.key,
      ).length;
      return held > want; // pair/single need — only give up a spare copy
    });
    if (!pick) break;
    const { given } = applyJokerExchange(state, playerIdx, pick.ownerIdx, pick.groupIdx);
    swaps.push({ given, ownerIdx: pick.ownerIdx });
  }
  return swaps;
}

// Does this player hold a complete, legal 2026 hand right now? Returns
// { hand, value } or null.
export function aiShouldDeclare(player) {
  const all = allTilesOf(player);
  if (all.length !== 14) return null;
  return validateWin(all, hasExposures(player));
}

export { tileName };

// Beginner-difficulty hint glow when no target is set: keep tiles that any
// fit-leading hand would want.
export function isUsefulTile(t, hand) {
  if (t.type === 'joker' || t.type === 'flower') return true;
  const need = handFit(hand, bestHands(hand)[0].hand).need;
  return (need.get(tileKey(t)) || 0) > 0;
}

export { SUITS };
